import { z } from 'zod';
import { airportOptionSchema, checkPassengerCount, clientFirstNameSchema, clientLastNameSchema, contactEmailSchema, contactPhoneSchema, DEFAULT_OPTION, flexibilityDaysSchema, futureDateSchema, newPassengerSchema, PASSENGER_COUNT_ERROR, travelClassSchema, } from './base-schemas';
// =====================================================================
// FLIGHT ESTIMATE SCHEMAS
// Validation schemas for flight estimate forms with French error messages
// =====================================================================
export const flightTypes = {
    MULTI_DESTINATION: 'MULTI_DESTINATION',
    ONE_WAY: 'ONE_WAY',
    ROUND_TRIP: 'ROUND_TRIP',
};
const flightTypeSchema = z.enum(['MULTI_DESTINATION', 'ONE_WAY', 'ROUND_TRIP']);
// ---- Company Preference Schema ----
const companyPreferenceSchema = z
    .object({
    companyName: z.object({ label: z.string(), value: z.string() }),
    hasPreference: z.boolean(),
})
    .refine((data) => !data.hasPreference || data.companyName.value.trim().length > 0, {
    message: 'Nom de la compagnie requis si préférence indiquée',
    path: ['companyName'],
});
// ---- Flight Leg Schema (shared building block) ----
const flightLegSchema = z.object({
    date: futureDateSchema,
    departure: airportOptionSchema,
    destination: airportOptionSchema,
    flexibilityAfter: flexibilityDaysSchema,
    flexibilityBefore: flexibilityDaysSchema,
});
// ---- Flight Estimate Form Base Schema ----
const flightEstimateFormBaseFields = {
    clientFirstName: clientFirstNameSchema,
    clientLastName: clientLastNameSchema,
    contactEmail: contactEmailSchema,
    companyPreference: companyPreferenceSchema,
    complementaryInformation: z.string(),
    contactPhoneNumber: contactPhoneSchema,
    existingPassengerIds: z.array(z.string()).optional(),
    maxStayDuration: z.coerce.number().int().min(0).optional(),
    newPassengers: z.array(newPassengerSchema).optional(),
    particularities: z.string(),
    travelClass: travelClassSchema,
    type: flightTypeSchema,
};
// ---- Discriminated Union Schema for Type-Safe Validation ----
// Each schema includes estimateType: 'FLIGHT' for the unified discriminated union
export const oneWayFlightEstimateSchema = z
    .object({
    ...flightEstimateFormBaseFields,
    estimateType: z.literal('FLIGHT'),
    flights: z.array(flightLegSchema).length(1, 'Un aller simple doit avoir exactement 1 vol'),
    type: z.literal('ONE_WAY'),
})
    .refine(checkPassengerCount, PASSENGER_COUNT_ERROR);
export const roundTripFlightEstimateSchema = z
    .object({
    ...flightEstimateFormBaseFields,
    estimateType: z.literal('FLIGHT'),
    flights: z
        .array(flightLegSchema)
        .length(2, 'Un aller-retour doit avoir exactement 2 vols')
        .refine((flights) => {
        // Length validation is handled by .length(2) - if not 2, skip date check
        if (flights.length !== 2)
            return true;
        const outbound = new Date(flights[0].date);
        const returnFlight = new Date(flights[1].date);
        return returnFlight > outbound;
    }, {
        message: 'La date de retour doit être après la date de départ',
        path: [1, 'date'],
    }),
    type: z.literal('ROUND_TRIP'),
})
    .refine(checkPassengerCount, PASSENGER_COUNT_ERROR);
export const multiDestinationFlightEstimateSchema = z
    .object({
    ...flightEstimateFormBaseFields,
    estimateType: z.literal('FLIGHT'),
    flights: z.array(flightLegSchema).min(2, 'Au moins 2 vols requis').max(10, 'Maximum 10 vols'),
    type: z.literal('MULTI_DESTINATION'),
})
    .refine(checkPassengerCount, PASSENGER_COUNT_ERROR);
// ---- Default Values ----
export const EMPTY_FLIGHT_LEG = {
    date: '',
    departure: DEFAULT_OPTION,
    destination: DEFAULT_OPTION,
    flexibilityAfter: 0,
    flexibilityBefore: 0,
};
